import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

import sliceLogo from '@/assets/slice-logo-v2-rotating.png';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided.');
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('verify-email-token', {
          body: { token }
        });

        if (error) {
          console.error('Verification error:', error);
          setStatus('error');
          setErrorMessage(error.message || 'Failed to verify email.');
          return;
        }

        if (data?.success) {
          setStatus('success');
          setVerifiedEmail(data.email || '');
          toast.success('Email verified successfully!');
        } else {
          setStatus('error');
          setErrorMessage(data?.error || 'Failed to verify email.');
        }
      } catch (err: any) {
        console.error('Verification error:', err);
        setStatus('error');
        setErrorMessage('An unexpected error occurred.');
      }
    };

    verifyToken();
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/auth');
  };

  const handleResendVerification = () => {
    navigate('/verification-expired');
  };

  return (
    <div className="relative min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo and Header */}
        <div className="text-center space-y-2">
          <div className="flex flex-col items-center justify-center space-y-2 mb-4">
            <img src={sliceLogo} alt="TheSlice Logo" className="h-12 w-12 animate-spin-slow" />
            <span className="text-4xl font-bold text-primary">TheSlice</span>
          </div>
        </div>

        <Card className="robinhood-card">
          <CardHeader className="space-y-1 text-center">
            {status === 'loading' && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                </div>
                <CardTitle className="text-2xl">Verifying Your Email</CardTitle>
                <CardDescription>
                  Please wait while we verify your email address...
                </CardDescription>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                </div>
                <CardTitle className="text-2xl text-green-500">Email Verified!</CardTitle>
                <CardDescription>
                  {verifiedEmail ? (
                    <>Your email <strong>{verifiedEmail}</strong> has been verified successfully.</>
                  ) : (
                    <>Your email has been verified successfully.</>
                  )}
                </CardDescription>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="h-12 w-12 text-destructive" />
                </div>
                <CardTitle className="text-2xl text-destructive">Verification Failed</CardTitle>
                <CardDescription>
                  {errorMessage || 'We could not verify your email address.'}
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {status === 'success' && (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  Your account is now active. You can sign in to access TheSlice.
                </p>
                <Button onClick={handleContinue} className="w-full robinhood-button">
                  Continue to Sign In
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  The verification link may have expired or already been used.
                </p>
                <div className="flex flex-col gap-2">
                  <Button onClick={handleResendVerification} className="w-full robinhood-button">
                    <Mail className="mr-2 h-4 w-4" />
                    Request New Verification Email
                  </Button>
                  <Button onClick={handleContinue} variant="outline" className="w-full">
                    Back to Sign In
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
